import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { firebaseAuthService } from '../firebase/auth';
import { getLogger } from '../logger';
import type {
  SignalRConfig,
  SignalREventHandlers,
  SignalREventName,
  SignalRConnectionState,
  SignalRService,
} from './types';

export class SignalRHubService implements SignalRService {
  private hubConnection: HubConnection | null = null;
  private config: SignalRConfig;
  private eventHandlers: Map<
    SignalREventName,
    Set<SignalREventHandlers[SignalREventName]>
  > = new Map();
  private connectionState: SignalRConnectionState = {
    isConnected: false,
    isConnecting: false,
    isReconnecting: false,
    connectionId: null,
    error: null,
  };

  constructor(config: SignalRConfig) {
    this.config = config;
  }

  public async startConnection(): Promise<void> {
    if (this.hubConnection?.state === HubConnectionState.Connected) {
      return;
    }

    if (this.hubConnection != null) {
      await this.stopConnection();
    }

    this.updateConnectionState({
      isConnecting: true,
      error: null,
    });

    try {
      this.hubConnection = new HubConnectionBuilder()
        .withUrl(`${this.config.baseUrl}/${this.config.hubName}`, {
          accessTokenFactory: () => this.getToken(),
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build();

      this.setupEventHandlers();
      await this.hubConnection.start();

      this.updateConnectionState({
        isConnected: true,
        isConnecting: false,
        isReconnecting: false,
        connectionId: this.hubConnection.connectionId,
        error: null,
      });
    } catch (error) {
      const logger = getLogger();
      logger.error('SignalR connection failed', {
        error: error instanceof Error ? error.message : String(error),
      });
      this.updateConnectionState({
        isConnected: false,
        isConnecting: false,
        isReconnecting: false,
        error: error instanceof Error ? error.message : 'Connection failed',
      });
      throw error;
    }
  }

  public async stopConnection(): Promise<void> {
    if (this.hubConnection?.state === HubConnectionState.Connected) {
      await this.hubConnection.stop();
    }
    this.hubConnection = null;
    this.updateConnectionState({
      isConnected: false,
      isConnecting: false,
      isReconnecting: false,
      connectionId: null,
      error: null,
    });
  }

  public async reconnect(): Promise<void> {
    await this.stopConnection();
    await this.startConnection();
  }

  public getConnectionState(): SignalRConnectionState {
    return { ...this.connectionState };
  }

  public on<T extends SignalREventName>(
    eventName: T,
    handler: SignalREventHandlers[T]
  ): void {
    if (!this.eventHandlers.has(eventName)) {
      this.eventHandlers.set(eventName, new Set());
    }
    this.eventHandlers.get(eventName)!.add(handler);

    // If connection is already established, register the handler immediately
    if (this.hubConnection?.state === HubConnectionState.Connected) {
      this.hubConnection.on(eventName, handler);
    }
  }

  public off<T extends SignalREventName>(
    eventName: T,
    handler: SignalREventHandlers[T]
  ): void {
    const handlers = this.eventHandlers.get(eventName);
    if (handlers) {
      handlers.delete(handler);
    }

    // If connection is established, unregister the handler immediately
    if (this.hubConnection?.state === HubConnectionState.Connected) {
      this.hubConnection.off(eventName, handler);
    }
  }

  private async getToken(): Promise<string> {
    const token = await firebaseAuthService.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    return token;
  }

  private setupEventHandlers(): void {
    if (!this.hubConnection) return;

    // Register all stored event handlers
    this.eventHandlers.forEach((handlers, eventName) => {
      handlers.forEach((handler) => {
        this.hubConnection!.on(eventName, handler);
      });
    });

    // Handle connection state changes
    this.hubConnection.onclose((error) => {
      this.updateConnectionState({
        isConnected: false,
        isConnecting: false,
        isReconnecting: false,
        error: error ? error.message : null,
      });
    });

    this.hubConnection.onreconnecting((error) => {
      this.updateConnectionState({
        isConnected: false,
        isConnecting: false,
        isReconnecting: true,
        error: error ? error.message : null,
      });
    });

    this.hubConnection.onreconnected((connectionId) => {
      this.updateConnectionState({
        isConnected: true,
        isConnecting: false,
        isReconnecting: false,
        connectionId,
        error: null,
      });
    });
  }

  private updateConnectionState(
    updates: Partial<SignalRConnectionState>
  ): void {
    this.connectionState = { ...this.connectionState, ...updates };
  }
}
